import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { userApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const VerifyEmail: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
    const [error, setError] = useState<string>("");
    const { firebaseUser, refreshAuthState } = useAuth();

    useEffect(() => {
        const verifyEmail = async () => {
            const token = searchParams.get("token");
            
            if (!token) {
                setStatus("error");
                setError("No verification token provided");
                return;
            }

            try {
                // Call the backend to verify the email
                await userApi.verifyEmailWithToken(token);
                setStatus("success");
                
                // Refresh auth state to pick up the verification status change
                await refreshAuthState();
                
                // Redirect after 3 seconds
                setTimeout(() => {
                    // If user is already signed in, go to profile instead of sign in
                    if (firebaseUser) {
                        navigate("/profile");
                    } else {
                        navigate("/signin?emailVerified=true");
                    }
                }, 3000);
            } catch (err: any) {
                setStatus("error");
                setError(err.response?.data?.detail || "Failed to verify email");
            }
        };

        verifyEmail();
    }, [searchParams, navigate, firebaseUser, refreshAuthState]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        Email Verification
                    </h2>
                </div>

                {status === "loading" && (
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Verifying your email...</p>
                    </div>
                )}

                {status === "success" && (
                    <div className="rounded-md bg-green-50 p-4">
                        <div className="text-center">
                            <svg
                                className="mx-auto h-12 w-12 text-green-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <h3 className="mt-2 text-lg font-medium text-green-800">
                                Email verified successfully!
                            </h3>
                            <p className="mt-1 text-sm text-green-700">
                                {firebaseUser ? "Redirecting you to your profile..." : "Redirecting you to sign in..."}
                            </p>
                        </div>
                    </div>
                )}

                {status === "error" && (
                    <div className="rounded-md bg-red-50 p-4">
                        <div className="text-center">
                            <svg
                                className="mx-auto h-12 w-12 text-red-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                            <h3 className="mt-2 text-lg font-medium text-red-800">
                                Verification failed
                            </h3>
                            <p className="mt-1 text-sm text-red-700">{error}</p>
                            <div className="mt-4 space-y-2">
                                <button
                                    onClick={() => navigate("/signin")}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                                >
                                    Go to Sign In
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VerifyEmail; 